/**
 * Typed GraphQL reads for a place's standings (place manager, unit 4).
 * Read-only: nothing in this module writes.
 *
 * Standings exist per season and per event only: the backend has no
 * place-wide leaderboard. So the page reads each season's leaderboard
 * (`Season.leaderboard`, one per season), for the seasons unit 2 reads, from
 * the place's one shared seasons read (`PlaceSeasonsQuery` in
 * `place-events-api.ts`). One read per season, side by side, so one season not
 * reading leaves the others standing.
 *
 * `Leaderboard.rankings(first, offset)` is a plain list (no `totalCount`),
 * best first. `rank` is the backend's own: every scorer gets a distinct rank,
 * ties going to whoever reached the score first (ROW_NUMBER over score, then
 * the time of the score). Inactive accounts are left out before ranking, and
 * a scorer whose account does not load is skipped, which can leave a gap in
 * the ranks. `score` is the season's points, an integer. The read asks for one
 * more than the page shows, so it can say "more have scored" without a count
 * it does not have.
 *
 * Who can read it: no viewer check from `season(id)` down to the rankings;
 * `season(id)` reads null only for an archived season, to anyone but staff.
 *
 * A season with no leaderboard row: `Season.leaderboard` is non-null, so its
 * resolver throws "No leaderboard found for season …" (`Season.kt`). That is
 * a season with no standings, not a failed read: the read says it as none
 * scored ({@link rankingsOrNone}).
 */

import { graphql, gqlRequestAs, type RequestOptions, type ResultOf } from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';
import { rankingsOrNone } from '@/features/places/standings';

/** A season's top scorers, best first: rank, points and face, eleven of them
 *  (`STANDINGS_SHOWN` + 1 in `standings.ts`, tied by a test). */
export const SeasonStandingsFields = graphql(`
  fragment SeasonStandingsFields on Season {
    id
    leaderboard {
      id
      rankings(first: 11) {
        rank
        score
        user {
          id
          public {
            displayName
            username
            photoURL
          }
        }
      }
    }
  }
`);

export const SeasonStandingsQuery = graphql(
  `
  query SeasonStandings($id: ID!) {
    season(id: $id) {
      ...SeasonStandingsFields
    }
  }
`,
  [SeasonStandingsFields],
);

/** A season's top scorers, as the read returns them. */
export type SeasonStandingsData = ResultOf<typeof SeasonStandingsFields>['leaderboard']['rankings'];

/** A season's top scorers. Null when the season does not exist or is archived
 *  (to anyone but staff): not readable by this viewer. Empty when the season
 *  has no leaderboard ({@link rankingsOrNone}). */
export async function fetchSeasonStandings(
  seasonId: string,
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<SeasonStandingsData | null> {
  return rankingsOrNone(
    gqlRequestAs(viewer, SeasonStandingsQuery, { id: seasonId }, options).then((data) => data.season?.leaderboard.rankings ?? null),
  );
}
